// ABOUTME: Utility functions for filtering goal lists on the child dashboard.
// ABOUTME: Centralizes the logic for what counts as "active" vs "uncelebrated" goals.

import { GoalStatus } from '../types/api';
import type { Goal, GoalProgressIndicator as GoalProgressData } from '../types/api';

/**
 * Returns true if goal is finished (Completed or Celebrated).
 * Matches the backend IsCompleted computed property semantics.
 */
export function isGoalFinished(goal: { status: GoalStatus }): boolean {
  return goal.status === GoalStatus.Completed || goal.status === GoalStatus.Celebrated;
}

/**
 * Returns goals that are active (not completed and not failed).
 * Consistent with the parent dashboard backend which filters by GoalStatus.Active.
 *
 * Generic so it preserves subtypes (e.g. TransformedGoal extends Goal).
 */
export function filterActiveGoals<T extends Goal>(goals: T[]): T[] {
  return goals.filter(g => g.status === GoalStatus.Active);
}

/**
 * Returns goals that are completed but have not yet been celebrated.
 * Failed goals are naturally excluded because their status is Failed, not Completed.
 */
export function filterUncelebratedGoals<T extends Goal>(goals: T[]): T[] {
  return goals.filter(g => g.status === GoalStatus.Completed);
}

/**
 * Sorts goals by completion progress ratio descending (highest % first).
 * Returns a new array; does not mutate the input.
 * Goals with targetValue <= 0 or undefined currentProgress sort to the bottom.
 */
export function sortGoalsByProgress<T extends Goal>(goals: T[]): T[] {
  return [...goals].sort((a, b) => {
    const aProgress = a.targetValue > 0 ? (a.currentProgress ?? 0) / a.targetValue : 0;
    const bProgress = b.targetValue > 0 ? (b.currentProgress ?? 0) / b.targetValue : 0;
    return bProgress - aProgress;
  });
}

/**
 * Returns the active goal closest to completion (highest progress ratio), or undefined
 * if the list is empty. Used for the "next goal" mini card on the All Kids dashboard tab
 * (issue #1900). A zero targetValue counts as 0% rather than dividing by zero.
 */
export function pickClosestToCompletionGoal(goals: GoalProgressData[]): GoalProgressData | undefined {
  if (goals.length === 0) return undefined;
  return goals.reduce((closest, goal) => {
    const goalRatio = goal.targetValue > 0 ? goal.currentValue / goal.targetValue : 0;
    const closestRatio = closest.targetValue > 0 ? closest.currentValue / closest.targetValue : 0;
    return goalRatio > closestRatio ? goal : closest;
  });
}

/**
 * Convert a Goal (child dashboard format) to GoalProgressData (parent dashboard format).
 * Used by ChildContentRow to render goals consistently across both dashboards.
 */
export function goalToProgressIndicator(goal: Goal): GoalProgressData {
  const currentValue = goal.currentProgress ?? 0;
  return {
    goalId: goal.id,
    goalTitle: goal.title,
    rewardText: goal.reward || goal.rewardDescription || '',
    rewardImageUrl: goal.rewardImageUrl,
    goalType: goal.type,
    currentValue,
    targetValue: goal.targetValue,
    dayPasses: goal.dayPasses ?? 0,
    dayPassesUsed: goal.dayPassesUsed ?? 0,
    daysRemaining: goal.daysRemaining,
    endDate: goal.endDate ?? undefined,
    isRecurring: goal.isRecurring,
    isNukeExempt: goal.isNukeExempt,
    startDate: goal.startDate ?? undefined,
    completedAt: goal.completedAt,
    failedAt: goal.failedAt,
    celebratedAt: goal.celebratedAt,
    progressIcons: [],
    progressText: `${currentValue}/${goal.targetValue}`,
  };
}
